import type { LayersList } from '@deck.gl/core';
import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';

/** A single numbered marker dropped on a building centroid. `position` is [lng, lat]. */
export interface MeterMarkerPoint {
    position: [number, number];
    /** The meter number(s) shown in the badge, e.g. "1" or "1, 2". */
    label: string;
}

// admi-dunkelblau, fully opaque — stands out against the satellite basemap and
// the red/green/yellow building fills from BuildingsDeckLayer.
const MARKER_FILL: [number, number, number, number] = [6, 34, 125, 255];
const WHITE: [number, number, number, number] = [255, 255, 255, 255];

/**
 * Round, opaque, numbered markers placed on building centroids. Returns a
 * ScatterplotLayer (the badge) plus a TextLayer (the number) so the digit sits
 * on top of the circle. Sizes are in pixels so the badge stays legible at any
 * zoom. Returns an empty list when there is nothing to draw.
 */
export function buildMeterMarkerLayers(points: MeterMarkerPoint[]): LayersList {
    if (points.length === 0) return [];
    return [
        new ScatterplotLayer<MeterMarkerPoint>({
            id: 'meter-markers-bg',
            data: points,
            getPosition: (p: MeterMarkerPoint) => p.position,
            getRadius: 16,
            radiusUnits: 'pixels',
            getFillColor: MARKER_FILL,
            stroked: true,
            getLineColor: WHITE,
            lineWidthUnits: 'pixels',
            getLineWidth: 2,
            pickable: false,
        }),
        new TextLayer<MeterMarkerPoint>({
            id: 'meter-markers-text',
            data: points,
            getPosition: (p: MeterMarkerPoint) => p.position,
            getText: (p: MeterMarkerPoint) => p.label,
            getColor: WHITE,
            getSize: 15,
            sizeUnits: 'pixels',
            fontWeight: 700,
            getTextAnchor: 'middle',
            getAlignmentBaseline: 'center',
            // Build the glyph atlas from the actual labels so digits, comma and
            // space all render regardless of the default character set.
            characterSet: 'auto',
            pickable: false,
        }),
    ];
}
